type AdjustmentType = 'PERCENTAGE' | 'FIXED_AMOUNT';

interface SellingPlanPaymentOption {
  id: string;
  discount: number;
  discountType: AdjustmentType;
  label: string;
  orders: number;
}

interface BillingPolicy {
  __typename: string;
  interval: string;
  intervalCount: number;
  maxCycles?: number;
  minCycles?: number;
}

export interface DeliveryPolicy {
  __typename: string;
  interval: string;
  intervalCount: number;
}

interface PricingPolicy {
  __typename: string;
  adjustmentType: AdjustmentType;
  adjustmentValue: {
    percentage?: number;
    amount?: number;
    currencyCode?: string;
  };
}

interface PricingPolicyV3 {
  paymentOptionId: string;
  amount: number;
  currencyCode: string;
}

export interface SellingPlan {
  id: string;
  name: string;
  options: string[];
  position: number;
  billingPolicy: BillingPolicy;
  deliveryPolicy: DeliveryPolicy & Batching;
  pricingPolicies: PricingPolicy[];
}

interface SellingPlanProduct {
  __typename: string;
  id: string;
  title: string;
  totalInventory: number;
  featuredImage: {
    altText?: string | null;
    id: string;
    transformedSrc: string;
    height: number;
    width: number;
  };
}

export interface SellingPlanMultiOrderPaymentOption {
  autorenew: AutorenewTypes;
  label: string;
  orders: number;
  id?: string;
  isGift?: boolean;
}

export interface SellingPlanGroup {
  id: string;
  name: string;
  description?: string;
  merchantCode: string;
  position: number;
  options: string[];
  type: SPGTypes;
  paymentOptions: SellingPlanPaymentOption[];
  prepaidDeliveryPolicy: DeliveryPolicy;
  prepaidOptions: string[];
  pricingPolicies?: { value: string }[];
  products: SellingPlanProduct[];
  sellingPlans: SellingPlan[];
  batching: Batching;
  multiOrderPaymentOption: SellingPlanMultiOrderPaymentOption;
  prepaidType?: PrepaidTypes;
  maxCycles: {
    enabled: boolean;
    maxCycles: number;
  };
}

export interface LocationGroupZone {
  zone: {
    id: string;
    name: string;
    countries: {
      name: string;
    }[];
  };
}

export interface ProfileLocationGroup {
  locationGroup: {
    id: string;
  };
  locationGroupZones: LocationGroupZone[];
}

export interface DeliveryProfile {
  id: string;
  name: string;
  sellingPlanGroups: SellingPlanGroup[];
  profileLocationGroups: ProfileLocationGroup[];
}

type FulfillmentOrderStatus =
  | 'CANCELLED'
  | 'CLOSED'
  | 'INCOMPLETE'
  | 'IN_PROGRESS'
  | 'ON_HOLD'
  | 'OPEN'
  | 'SCHEDULED';

interface FulfillmentOrder {
  id: string;
  status: FulfillmentOrderStatus;
  fulfillAt: string;
}

export interface OrderFulfillments {
  id: string;
  name: string;
  displayFulfillmentStatus: string;
  fulfillmentOrders: FulfillmentOrder[];
}

export enum BatchAnchors {
  Monthday = 'MONTHDAY',
  Weekday = 'WEEKDAY',
  Yearday = 'YEARDAY',
}

enum BatchPreAnchors {
  Asap = 'ASAP',
  Next = 'NEXT',
}

export enum SPGTypes {
  Classic = 'classic',
  Prepaid = 'prepaid',
  Trial = 'trial',
}

export enum PrepaidTypes {
  Multi = 'MULTI',
  Single = 'SINGLE',
}

export enum AutorenewTypes {
  Enabled = 'ENABLED',
  Disabled = 'DISABLED',
}

export interface Batching {
  id?: string;
  minimumDaysBetweenOrders?: number;
  enabled: boolean;
  cutOffDays?: number;
  anchor: BatchAnchors;
  anchorWeek: string[];
  anchorMonth: string[];
  anchorYear: { month: string; day: string }[];
  preAnchorBehavior?: BatchPreAnchors;
}

// For V3, we will not differenciate multi order and single order
// however we still need to support it for backward compatibility
export interface PrepaidSubscriptionsV2Form
  extends Omit<
    SellingPlanGroup,
    'multiOrderPaymentOption' | 'paymentOptions' | 'pricingPolicies'
  > {
  paymentOptions: SellingPlanMultiOrderPaymentOption[];
  multiOrderPaymentOption: SellingPlanMultiOrderPaymentOption[];
  pricingPolicies: PricingPolicyV3[];
}
